import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from 'sonner'
import { Loader2, AlertCircle, Info } from 'lucide-react'
import { csrfHeaders } from '@/lib/csrf'

interface AddByodDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: number
}

export function AddByodDialog({
  open,
  onOpenChange,
  projectId,
}: AddByodDialogProps) {
  const { t } = useTranslation('conversations')
  const [domainInput, setDomainInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setDomainInput('')
      setError(null)
      setIsLoading(false)
    }
  }, [open])

  const handleAddDomain = async () => {
    if (!domainInput.trim()) {
      setError(t('domain.toasts.enterDomainName'))
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch(`/projects/${projectId}/custom_domain/add_byod`, {
        method: 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify({
          domain: domainInput.trim(),
        }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        toast.success(t('domain.toasts.domainAddedSuccess'))
        onOpenChange(false)
        router.reload({ only: ['project'] })
      } else {
        const errorMsg = data.errors?.join(', ') || data.error || t('domain.toasts.domainAddError')
        setError(errorMsg)
        toast.error(errorMsg)
      }
    } catch (error) {
      console.error('Add BYOD domain error:', error)
      const errorMsg = t('domain.toasts.networkErrorAdd')
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('domain.byod.inputTitle')}</DialogTitle>
          <DialogDescription>{t('domain.byod.inputDescription')}</DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 -mx-6 px-6">
          <div className="space-y-4 pb-4">
            <div className="space-y-2">
              <Label htmlFor="domain">{t('domain.byod.domainLabel')}</Label>
              <Input
                id="domain"
                placeholder={t('domain.buyDialog.domainPlaceholder')}
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddDomain()
                  }
                }}
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
              </div>
            )}

            <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800 dark:text-blue-300">
                <p className="font-medium mb-1">{t('domain.byod.requirementsTitle')}</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>{t('domain.byod.requirement1')}</li>
                  <li>{t('domain.byod.requirement2')}</li>
                  <li>{t('domain.byod.requirement3')}</li>
                </ul>
              </div>
            </div>
          </div>

          <DialogFooter className="sticky bottom-0 bg-background border-t pt-4 -mx-6 px-6">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('common:cancel')}
            </Button>
            <Button onClick={handleAddDomain} disabled={isLoading || !domainInput.trim()}>
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('domain.byod.addingDomain')}
                </>
              ) : (
                t('domain.byod.continue')
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
